if (!customElements.get('progress-bar')) {

  class ProgressBar extends HTMLElement {

    constructor() {
      super();

      this.options = {
        gift_enabled: false,
        shipping_enabled: false,
        currency: {
          name: 'British Pound',
          iso_code: 'GBP',
          symbol: '£'
        },
        translations: {
          gift_threshold_text: '{{ html }} away from a free gift',
          shipping_threshold_text: '{{ html }} away from free shipping',
          reached_text: 'You have reached free shipping'
        }
      };

      this.elements = {};
    }

    decodeHtmlEntities(text) {
      if (!text || typeof text !== 'string') {
        return text;
      }

      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }

    decodeTranslationStrings() {
      if (!this.options.translations) {
        return;
      }

      Object.keys(this.options.translations).forEach((key) => {
        this.options.translations[key] = this.decodeHtmlEntities(this.options.translations[key]);
      });
    }

    loadOptions() {
      const optionsScript = this.querySelector('[data-progress-bar-options]');

      if (optionsScript?.textContent?.trim()) {
        const dataOptions = parseJson(optionsScript.textContent);
        this.options = {
          ...this.options,
          ...dataOptions
        };
      } else if (this?.dataset?.options) {
        const dataOptions = parseJson(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions
        };
      }

      this.decodeTranslationStrings();
    }

    cacheElements() {
      this.elements = {
        content: this.querySelector('[data-progress-bar-content]'),
        header: this.querySelector('[data-progress-bar-header]'),
        bar: this.querySelector('[data-progress-bar]'),
        barInner: this.querySelector('[data-progress-bar-inner]')
      };
    }

    connectedCallback() {
      this.loadOptions();
      this.cacheElements();
      ProgressBar.bindDrawerListener();
      this.initializeProgressBar();
    }

    hasGiftThreshold() {
      return this.options.gift_enabled && this.options.gift_threshold;
    }

    hasShippingThreshold() {
      return this.options.shipping_enabled && this.options.shipping_threshold;
    }

    showProgressBar() {
      this.classList.remove('progress-bar--hidden');
      this.refreshHeight();
    }

    hideProgressBar() {
      this.classList.add('progress-bar--hidden');
      this.style.removeProperty('height');
    }

    refreshHeight() {
      if (this.classList.contains('progress-bar--hidden') || !this.elements.content) {
        return;
      }

      const contentHeight = this.elements.content.offsetHeight;

      if (contentHeight > 0) {
        this.style.height = `${contentHeight}px`;
      } else {
        this.style.removeProperty('height');
      }
    }

    static bindDrawerListener() {
      if (ProgressBar.drawerListenerBound) {
        return;
      }

      const refreshVisibleBars = () => {
        document.querySelectorAll('progress-bar:not(.progress-bar--hidden)').forEach((progressBar) => {
          if (typeof progressBar.refreshHeight === 'function') {
            progressBar.refreshHeight();
          }
        });
      };

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'class' && document.body.classList.contains('cart-drawer-is-open')) {
            requestAnimationFrame(refreshVisibleBars);
            break;
          }
        }
      });

      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      ProgressBar.drawerListenerBound = true;
    }

    initializeProgressBar() {
      let currentCartTotal = 0;

      if (this.dataset.cartTotal) {
        currentCartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      } else if (this.options.cart_total) {
        currentCartTotal = parseInt(this.options.cart_total, 10) || 0;
      } else {
        const cartTotalElement = document.querySelector('[data-cart-total]');
        if (cartTotalElement) {
          currentCartTotal = parseInt(cartTotalElement.getAttribute('data-cart-total'), 10) || 0;
        }
      }

      if (currentCartTotal === 0 && typeof window.cart !== 'undefined' && window.cart.total_price) {
        currentCartTotal = window.cart.total_price;
      }

      if (!this.elements.content || !this.elements.header || !this.elements.barInner) {
        return;
      }

      this.update({ total_price: currentCartTotal });
    }

    update(state) {
      const { total_price } = state;

      if (!this.hasGiftThreshold() && !this.hasShippingThreshold()) {
        return;
      }

      const giftThreshold = this.hasGiftThreshold() ? this.options.gift_threshold : 0;
      const shippingThreshold = this.hasShippingThreshold() ? this.options.shipping_threshold : 0;
      const highestPrice = Math.max(giftThreshold, shippingThreshold, 1);

      const giftMet = !this.hasGiftThreshold() || total_price >= giftThreshold;
      const shippingMet = !this.hasShippingThreshold() || total_price >= shippingThreshold;

      let type = 'gift';

      if (giftMet && shippingMet) {
        type = 'completed';
      } else if (!this.hasGiftThreshold()) {
        type = 'shipping';
      } else if (
        this.hasGiftThreshold()
        && this.hasShippingThreshold()
        && giftThreshold <= shippingThreshold
        && total_price >= giftThreshold
      ) {
        type = 'shipping';
      } else if (
        this.hasGiftThreshold()
        && this.hasShippingThreshold()
        && giftThreshold > shippingThreshold
        && total_price < shippingThreshold
      ) {
        type = 'shipping';
      }

      if (type == 'gift') {
        const price_to_go = giftThreshold - total_price;
        const price_to_go_html = `${this.options.currency.symbol}${(Math.round(price_to_go) / 100).toFixed(2).replace('.00', '')}`;
        const procent = Math.round((total_price * 100) / highestPrice);
        const header_text = this.options.translations.gift_threshold_text.replace('{{ html }}', price_to_go_html);

        this.elements.header.innerText = header_text;
        this.elements.barInner.style.width = `${procent}%`;
        this.elements.bar.classList.remove('progress-bar__bar--reached');

        if (this.hasShippingThreshold() && total_price < shippingThreshold) {
          this.elements.bar.classList.remove('progress-bar__bar--step-2');
        } else {
          this.elements.bar.classList.add('progress-bar__bar--step-2');
        }

        this.showProgressBar();
      } else if (type == 'shipping') {
        const price_to_go = shippingThreshold - total_price;
        const price_to_go_html = `${this.options.currency.symbol}${(Math.round(price_to_go) / 100).toFixed(2).replace('.00', '')}`;
        const procent = Math.round((total_price * 100) / highestPrice);
        const header_text = this.options.translations.shipping_threshold_text.replace('{{ html }}', price_to_go_html);

        this.elements.header.innerText = header_text;
        this.elements.barInner.style.width = `${procent}%`;
        this.elements.bar.classList.remove('progress-bar__bar--reached');

        if (this.hasGiftThreshold() && total_price < giftThreshold) {
          this.elements.bar.classList.remove('progress-bar__bar--step-2');
        } else {
          this.elements.bar.classList.add('progress-bar__bar--step-2');
        }

        this.showProgressBar();
      } else {
        this.elements.header.innerText = this.options.translations.reached_text;
        this.elements.barInner.style.width = '100%';
        this.elements.bar.classList.add('progress-bar__bar--reached');
        this.elements.bar.classList.remove('progress-bar__bar--step-2');
        this.hideProgressBar();
      }
    }

  }

  window.ProgressBar = ProgressBar;

  customElements.define('progress-bar', ProgressBar);

}

/**
 * Update all progress-bar custom elements (cart drawer, cart page).
 * Waits for the custom element when needed; no-ops if none are present.
 */
function updateProgressBars(state, root = document) {
  const progressBars = root.querySelectorAll('progress-bar');

  if (!progressBars.length) {
    return;
  }

  const run = () => {
    progressBars.forEach((progressBar) => {
      if (typeof progressBar.update === 'function') {
        progressBar.update(state);
      }
    });
  };

  if (customElements.get('progress-bar')) {
    run();
  } else {
    customElements.whenDefined('progress-bar').then(run);
  }
}

window.updateProgressBars = updateProgressBars;
